import React from 'react';
import { Box, Button, Stack, Typography, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import DescriptionIcon from '@mui/icons-material/Description';
import BuildIcon from '@mui/icons-material/Build';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import DirectionsIcon from '@mui/icons-material/Directions';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RouteIcon from '@mui/icons-material/Route';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SchoolIcon from '@mui/icons-material/School';
import BadgeIcon from '@mui/icons-material/Badge';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '44vh',
  width: '100%',
  padding: theme.spacing(5),
  textAlign: 'center',
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: 88,
  height: 88,
  borderRadius: 16,
  backgroundColor: theme.palette.success.light,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2.5),
  color: theme.palette.success.contrastText,
  boxShadow: `0 12px 28px ${theme.palette.success.main}22`,
  '& svg': {
    color: 'inherit',
  },
}));

interface CustomEmptyStateProps {
  resource: string;
  title?: string;
  subtitle?: string;
  actionText?: string;
  onAction?: () => void;
  icon?: React.ReactNode;
  showBackground?: boolean;
}

const getResourceDetails = (resource: string) => {
  switch (resource) {
    case 'drivers':
      return {
        icon: <DriveEtaIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Drivers Yet',
        subtitle:
          'Start building your driver database by adding your first driver profile. You can manage licenses, certifications, and employment history.',
        actionText: 'Create Driver Profile',
      };
    case 'users':
      return {
        icon: <PersonAddIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Users Yet',
        subtitle:
          'Add users to your system to manage access and permissions. Users can be drivers, administrators, or other team members.',
        actionText: 'Add User',
      };
    case 'equipment':
      return {
        icon: <BuildIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Equipment Yet',
        subtitle:
          'Track your fleet equipment, vehicles, and assets. Manage maintenance schedules and equipment assignments.',
        actionText: 'Add Equipment',
      };
    case 'sites':
    case 'crm/locations':
      return {
        icon: <LocationOnIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Sites Yet',
        subtitle:
          'Add property sites and zones so crews know where work happens, what areas to maintain, and how each location should be serviced.',
        actionText: 'Add Site',
      };
    case 'routes':
      return {
        icon: <RouteIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Routes Yet',
        subtitle:
          'Routes will appear here once you group scheduled work by geography, crew, or service day.',
        actionText: 'Create Route',
      };
    case 'driver-licenses':
      return {
        icon: <VerifiedUserIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Driver Licenses',
        subtitle:
          'License records will appear here after driver profiles include license details, classes, and expiry dates.',
        actionText: 'Add License',
      };
    case 'driver-vehicles':
      return {
        icon: <DriveEtaIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Vehicle Experience',
        subtitle:
          'Vehicle experience will appear here after drivers add the equipment types and operating history they can support.',
        actionText: 'Add Vehicle Experience',
      };
    case 'driver-education':
      return {
        icon: <SchoolIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Education Records',
        subtitle:
          'Training and education records will appear here after drivers add completed programs or supporting documents.',
        actionText: 'Add Education',
      };
    case 'driver-certifications':
      return {
        icon: <BadgeIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Certifications',
        subtitle:
          'Certification records will appear here after drivers add credentials, issuing organizations, and expiry dates.',
        actionText: 'Add Certification',
      };
    case 'forms':
      return {
        icon: <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Forms Yet',
        subtitle:
          'Create custom forms for data collection, inspections, and documentation. Build your workflow with digital forms.',
        actionText: 'Create Form',
      };
    case 'completed-forms':
      return {
        icon: <AssignmentIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Completed Forms',
        subtitle:
          'Form submissions and completed responses will appear here. Track progress and review submitted data.',
        actionText: 'View Forms',
      };
    case 'customers':
      return {
        icon: <BusinessIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Customers Yet',
        subtitle:
          'Build your customer database by adding your first customer. Track contact information, service history, and business relationships.',
        actionText: 'Add Customer',
      };
    case 'leads':
      return {
        icon: <PersonAddIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Leads Yet',
        subtitle:
          'Start generating business by adding potential customers. Track lead sources, status, and convert them into customers.',
        actionText: 'Add Lead',
      };
    case 'opportunities':
      return {
        icon: <TrendingUpIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Opportunities Yet',
        subtitle:
          'Track potential business opportunities and sales pipeline. Monitor deal progress and convert leads into revenue.',
        actionText: 'Create Opportunity',
      };
    case 'estimates':
      return {
        icon: <RequestQuoteIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Estimates Yet',
        subtitle:
          'Create professional estimates for your services. Track estimate status and convert approved estimates into invoices.',
        actionText: 'Create Estimate',
      };
    case 'invoices':
      return {
        icon: <ReceiptIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Invoices Yet',
        subtitle:
          'Generate and send professional invoices to customers. Track payment status and manage your billing process.',
        actionText: 'Create Invoice',
      };
    case 'payments':
    case 'crm/payments':
      return {
        icon: <PaymentIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Payments Yet',
        subtitle:
          'Payment records and transaction history will appear here. Track received payments and outstanding balances.',
        actionText: 'Record Payment',
      };
    case 'bills':
      return {
        icon: <ReceiptIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Bills Yet',
        subtitle:
          'Track expenses and vendor bills. Manage your payables and keep track of business expenses.',
        actionText: 'Add Bill',
      };
    case 'service-appointments':
      return {
        icon: <DirectionsIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Service Appointments',
        subtitle:
          'Schedule and manage service appointments with customers. Track service history and appointment status.',
        actionText: 'Schedule Appointment',
      };

    case 'service-providers':
      return {
        icon: <BusinessIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Service Providers Yet',
        subtitle:
          'Build your network of contractors and service providers. Track contact information and service capabilities.',
        actionText: 'Add Service Provider',
      };
    case 'driver-employment':
      return {
        icon: <PersonAddIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Employment History',
        subtitle:
          'Work history and employment records will appear here. Track previous employers, job roles, and references.',
        actionText: 'Add Employment',
      };
    case 'equipment-maintenance':
      return {
        icon: <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Maintenance Records',
        subtitle:
          'Equipment maintenance history and service records will appear here. Track repairs, inspections, and scheduled maintenance.',
        actionText: 'Add Maintenance',
      };
    case 'equipment-fuel':
      return {
        icon: <LocalShippingIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Fuel Records',
        subtitle:
          'Fuel consumption and cost tracking will appear here. Monitor fuel efficiency and manage fuel expenses.',
        actionText: 'Add Fuel Record',
      };
    case 'equipment-inspections':
      return {
        icon: <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Inspection Records',
        subtitle:
          'Safety inspections and compliance records will appear here. Stay current with regulatory requirements.',
        actionText: 'Add Inspection',
      };
    case 'equipment-assignments':
      return {
        icon: <LocalShippingIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Equipment Assignments',
        subtitle:
          'Equipment assignment history will appear here. Track who used which equipment and when.',
        actionText: 'Add Assignment',
      };
    case 'equipment-incidents':
      return {
        icon: <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Incident Reports',
        subtitle:
          'Incident reports and damage records will appear here. Document accidents, breakdowns, and safety issues.',
        actionText: 'Add Incident',
      };
    case 'workflows':
      return {
        icon: <AccountTreeIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Workflows Yet',
        subtitle:
          'Create workflows to organize and manage your tasks. Define statuses, categories, and processes for your business operations.',
        actionText: 'Create Workflow',
      };
    case 'boards':
      return {
        icon: <ViewKanbanIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Boards Yet',
        subtitle:
          'Create boards to organize sales, projects, leads, or custom workflows with stages your team can track visually.',
        actionText: 'Create Board',
      };
    case 'customer-types':
    case 'crm/customer-types':
      return {
        icon: <BusinessIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Customer Types Yet',
        subtitle:
          'Create customer types to segment accounts for reporting, quoting, and service workflows.',
        actionText: 'Create Customer Type',
      };
    case 'zones':
    case 'crm/zones':
      return {
        icon: <LocationOnIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Zones Yet',
        subtitle:
          'Define zones to group locations, route crews, and report on service coverage by area.',
        actionText: 'Create Zone',
      };
    case 'lot-sizes':
    case 'crm/code-tables/lot-sizes':
      return {
        icon: <FolderOpenIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Lot Sizes Yet',
        subtitle:
          'Add lot size options so estimates and service templates can classify work consistently.',
        actionText: 'Create Lot Size',
      };
    case 'service-blueprints':
    case 'crm/service-blueprints':
      return {
        icon: <BuildIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Service Blueprints Yet',
        subtitle:
          'Create reusable blueprints for services your team estimates, schedules, and completes often.',
        actionText: 'Create Blueprint',
      };
    case 'timetracking-activities':
    case 'timetracking/activities':
      return {
        icon: <AccessTimeIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: 'No Time Activities Yet',
        subtitle:
          'Define activity types for time tracking and timer segments. Crew can start timers and log time against these activities in the field.',
        actionText: 'Create Activity',
      };
    default:
      return {
        icon: <FolderOpenIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        title: `No ${resource.charAt(0).toUpperCase() + resource.slice(1)} Yet`,
        subtitle: `Start by adding your first ${resource.replace('-', ' ')} item.`,
        actionText: `Add ${resource.charAt(0).toUpperCase() + resource.slice(1)}`,
      };
  }
};

export const CustomEmptyState: React.FC<CustomEmptyStateProps> = ({
  resource,
  title,
  subtitle,
  actionText,
  onAction,
  icon,
  showBackground = true,
}) => {
  const defaults = getResourceDetails(resource);

  const finalTitle = title || defaults.title;
  const finalSubtitle = subtitle || defaults.subtitle;
  const finalActionText = actionText || defaults.actionText;
  const finalIcon = icon || defaults.icon;

  const content = (
    <EmptyStateContainer>
      <IconContainer>{finalIcon}</IconContainer>

      <Typography
        variant="h6"
        component="h2"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          mb: 1,
        }}
      >
        {finalTitle}
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          maxWidth: 480,
          lineHeight: 1.55,
          mb: 3,
        }}
      >
        {finalSubtitle}
      </Typography>

      {onAction && (
        <Stack direction="row" spacing={1.5} justifyContent="center">
          <Button
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            onClick={onAction}
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
              },
            }}
          >
            {finalActionText}
          </Button>
        </Stack>
      )}
    </EmptyStateContainer>
  );

  if (showBackground) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            backgroundColor: 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            maxWidth: '640px',
            width: '100%',
            margin: 'auto',
            overflow: 'hidden',
          }}
        >
          {content}
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
      }}
    >
      {content}
    </Box>
  );
};
